import { SkeletonText } from '@carbon/react';
import { LinkOrText } from '../link-or-text';
import type { EvaluationSummary, NRISource } from './types';
import style from './comparison-summary.module.scss';

const NRI_SOURCE_LABELS: Record<NRISource, string> = {
  stored: 'Eval 2.0',
  calculator: 'NRI Calculator',
};

interface ComparisonSummaryProps {
  evaluations: {
    a: EvaluationSummary;
    b: EvaluationSummary;
  } | null;
  isLoading: boolean;
}

/**
 * One eval identity column (Origin or Target) in the Package Evals banner:
 * the eval link, scenario, package, and NRI source.
 * @param {object} props - The column props
 * @param {string} props.label - Column label ("Origin" or "Target")
 * @param {EvaluationSummary} props.evaluation - The evaluation data
 * @return {JSX.Element} A single eval identity column
 */
const EvaluationColumn = ({
  label,
  evaluation,
}: {
  label: string;
  evaluation: EvaluationSummary;
}) => (
  <div className={style.evalColumn}>
    <p className={style.columnLabel}>{label}</p>
    <LinkOrText
      href={evaluation.href}
      className={`${style.link} ${style.evalId}`}
    >
      {`Eval ${evaluation.evaluationId}`}
    </LinkOrText>
    <LinkOrText href={evaluation.scenario.href} className={style.link}>
      {evaluation.scenario.name}
    </LinkOrText>
    <LinkOrText href={evaluation.package.href} className={style.link}>
      {evaluation.package.name}
    </LinkOrText>
    <p className={style.meta}>
      Source:{' '}
      <span className={style.metaValue}>
        {NRI_SOURCE_LABELS[evaluation.nriSource]}
      </span>
    </p>
  </div>
);

/**
 * Skeleton for one eval column while the comparison loads.
 * @param {object} props - The column props
 * @param {string} props.label - Column label ("Origin" or "Target")
 * @return {JSX.Element} A skeleton eval column
 */
const SkeletonColumn = ({ label }: { label: string }) => (
  <div className={style.evalColumn}>
    <p className={style.columnLabel}>{label}</p>
    <SkeletonText width="80px" />
    <SkeletonText width="90%" />
    <SkeletonText width="80%" />
    <SkeletonText width="120px" />
  </div>
);

/**
 * Package Evals banner: the Origin and Target evaluations shown side by side,
 * each with its eval link, scenario, package, and NRI source. Shows skeleton
 * columns during loading.
 * @param {ComparisonSummaryProps} props - The component props
 * @return {JSX.Element} The Package Evals banner
 */
export const ComparisonSummary = ({
  evaluations,
  isLoading,
}: ComparisonSummaryProps) => {
  return (
    <div className={style.banner}>
      <p className={style.bannerLabel}>Package Evals</p>
      {isLoading || !evaluations ? (
        <>
          <SkeletonColumn label="Origin" />
          <SkeletonColumn label="Target" />
        </>
      ) : (
        <>
          <EvaluationColumn label="Origin" evaluation={evaluations.a} />
          <EvaluationColumn label="Target" evaluation={evaluations.b} />
        </>
      )}
    </div>
  );
};
